import { useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CasinoCard } from "@/components/CasinoCard";
import { listCasinos } from "@/data/casinos";
import { InternalLinks } from "@/components/InternalLinks";

type Variant = "all" | "best" | "swedish";

type FilterState = {
  swedishLicense: boolean;
  noSwedishLicense: boolean;
  noSpelpaus: boolean;
  swish: boolean;
  bankid: boolean;
  trustly: boolean;
  zimpler: boolean;
  brite: boolean;
  crypto: boolean;
  q: string;
};

const initialFilter: FilterState = {
  swedishLicense: false,
  noSwedishLicense: false,
  noSpelpaus: false,
  swish: false,
  bankid: false,
  trustly: false,
  zimpler: false,
  brite: false,
  crypto: false,
  q: "",
};

export function CasinoListPage({ variant = "all" }: { variant?: Variant }) {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const { pathname } = useLocation();
  const [filter, setFilter] = useState<FilterState>(initialFilter);

  const titles: Record<Variant, string> = {
    all: isSv ? "Alla casinon i Sverige" : "All casinos in Sweden",
    best: isSv ? "Bästa casino i Sverige 2026" : "Best casinos in Sweden 2026",
    swedish: isSv ? "Svenska casinon" : "Swedish casinos",
  };
  const descriptions: Record<Variant, string> = {
    all: isSv
      ? "Jämför svenska och internationella casinon. Filtrera på licens, betalningsmetoder, Spelpaus och mer."
      : "Compare Swedish and international casinos. Filter by license, payments, Spelpaus and more.",
    best: isSv
      ? "Vår topplista över svenska casinon, jämförd på licens, säkerhet, betalningar och bonus."
      : "Our top list of Swedish casinos, compared on license, safety, payments and bonus.",
    swedish: isSv
      ? "Svenska casinon med svensk licens, BankID, Swish och anslutning till Spelpaus."
      : "Swedish casinos with a Swedish license, BankID, Swish and Spelpaus.",
  };

  if (pathname.endsWith("/casino-online") || pathname.endsWith("/online-casinos")) {
    titles.all = isSv ? "Casino online i Sverige" : "Online casinos in Sweden";
    descriptions.all = isSv ? "Casino online: jämför licens, betalningar och spelutbud." : "Online casinos: compare licences, payments and games.";
  }
  if (pathname.endsWith("/spelbolag") || pathname.endsWith("/sportsbooks")) {
    titles.all = isSv ? "Spelbolag i Sverige" : "Sportsbook directory for Sweden";
    descriptions.all = isSv ? "Översikt över spelbolag, licenser och betalningsmetoder." : "Browse operators, licences and payment methods for Sweden.";
  }

  const filtered = useMemo(
    () =>
      listCasinos({
        swedishLicense: filter.swedishLicense || variant === "swedish",
        noSwedishLicense: filter.noSwedishLicense,
        noSpelpaus: filter.noSpelpaus,
        swish: filter.swish,
        bankid: filter.bankid,
        trustly: filter.trustly,
        zimpler: filter.zimpler,
        brite: filter.brite,
        crypto: filter.crypto,
        searchQuery: filter.q || undefined,
      }).sort((a, b) => {
        if (a.priority_brand !== b.priority_brand) {
          return Number(b.priority_brand) - Number(a.priority_brand);
        }
        return b.overall_rating - a.overall_rating;
      }),
    [filter, variant],
  );

  return (
    <div className="container-page py-8">
      <SEO
        title={titles[variant]}
        description={descriptions[variant]}
      />
      <Breadcrumbs
        items={[
          { label: t("nav.home"), href: prefix },
          { label: titles[variant] },
        ]}
      />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{titles[variant]}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{descriptions[variant]}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="card h-fit p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink-900">{t("filters.title")}</h2>
            <button
              type="button"
              className="text-xs font-semibold text-brand-700"
              onClick={() => setFilter(initialFilter)}
            >
              {t("cta.clear")}
            </button>
          </div>
          <input
            type="search"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            placeholder={t("search.placeholder")}
            className="mt-3 w-full rounded-md border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
          <div className="mt-4 space-y-1.5 text-sm">
            {(
              [
                ["swedishLicense", t("label.swedishLicense")],
                ["noSwedishLicense", t("label.noSwedishLicense")],
                ["noSpelpaus", t("label.notSpelpaus")],
                ["swish", t("label.swish")],
                ["bankid", t("label.bankid")],
                ["trustly", t("label.trustly")],
                ["zimpler", t("label.zimpler")],
                ["brite", t("label.brite")],
                ["crypto", t("label.crypto")],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-700 focus:ring-brand-500"
                  checked={filter[key]}
                  onChange={(e) => setFilter((f) => ({ ...f, [key]: e.target.checked }))}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </aside>

        <div className="grid gap-3">
          <div className="flex items-center justify-between text-sm text-ink-600">
            <span>
              {filtered.length} {isSv ? "resultat" : "results"}
            </span>
            <span>
              {isSv ? "Sorterat på betyg och utvalda partners" : "Sorted by rating and featured partners"}
            </span>
          </div>
          {filtered.length === 0 ? (
            <div className="card p-8 text-center text-ink-500">{t("list.empty")}</div>
          ) : (
            filtered.map((c, i) => (
              <CasinoCard key={c.id} casino={c} rank={i + 1} source={`list-${variant}`} />
            ))
          )}
        </div>
      </div>

      <InternalLinks context="casino-list" />
    </div>
  );
}
